/**
 * Savings Index Card — "Índice Quitamelo Porque Me Lo Gasto"
 * Shows 10-25% suggestion and links to the 18-month challenge.
 */
import React from 'react';
import { Target, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const QUICK_PERCENTAGES = [10, 15, 20, 25];

const SavingsIndexCard = ({ indiceData, setIndiceData, calculateIndice, applyIndiceToChallenge18 }) => (
  <Card className="tool-card">
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Target className="w-6 h-6 text-neon" />
        Índice Quítamelo Porque Me Lo Gasto
      </CardTitle>
      <CardDescription>¿Cuánto te quitas tú mismo antes de que el universo te lo quite primero? 💸</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div>
        <label className="input-label">Ingreso mensual ($)</label>
        <Input
          type="number"
          placeholder="15000"
          value={indiceData.income}
          onChange={(e) => setIndiceData({ ...indiceData, income: e.target.value, percentage: 0 })}
          className="input-field"
          data-testid="indice-income-input"
        />
      </div>

      {parseFloat(indiceData.income) > 0 && (
        <div className="indice-suggestions" data-testid="indice-suggestions">
          <p className="indice-suggestions-label">
            💡 La regla de oro: ahorra entre el <strong>10%</strong> y el <strong>25%</strong>. Menos de eso es solo "buena intención"
          </p>
          <div className="indice-suggestions-range">
            <span className="indice-range-min">
              Mínimo (10%): <strong>${Math.round(parseFloat(indiceData.income) * 0.10).toLocaleString('es-MX')}</strong>
            </span>
            <span className="indice-range-max">
              Ideal (25%): <strong>${Math.round(parseFloat(indiceData.income) * 0.25).toLocaleString('es-MX')}</strong>
            </span>
          </div>
          <div className="indice-quick-chips">
            {QUICK_PERCENTAGES.map((pct) => (
              <button
                type="button"
                key={pct}
                onClick={() => setIndiceData({
                  ...indiceData,
                  auto: String(Math.round(parseFloat(indiceData.income) * (pct / 100))),
                  percentage: 0
                })}
                className="indice-quick-chip"
                data-testid={`indice-quick-chip-${pct}`}
              >
                {pct}%
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="input-label">Monto automático ($)</label>
        <Input
          type="number"
          placeholder="3000"
          value={indiceData.auto}
          onChange={(e) => setIndiceData({ ...indiceData, auto: e.target.value, percentage: 0 })}
          className="input-field"
          data-testid="indice-auto-input"
        />
      </div>
      <Button onClick={calculateIndice} className="w-full button-neon" data-testid="indice-calculate-button">
        Quítamelo y dime cuánto es
      </Button>
      {indiceData.percentage > 0 && (
        <div className="indice-result">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${Math.min(indiceData.percentage, 100)}%` }}
            />
          </div>
          <p className="indice-percentage">{indiceData.percentage}%</p>
          <p className="text-sm text-gray-400 mt-2">
            {indiceData.percentage < 5 ? '🚨 Esto no es ahorro, es propina al banco. Sube ese número.' :
              indiceData.percentage < 10 ? '😅 Estás tibio. El mínimo decente es 10%. Vamos, no te creas pobre.' :
                indiceData.percentage < 20 ? '👌 Vas bien. Ahora la pregunta es: ¿puedes apretarte un poco más?' :
                  indiceData.percentage < 30 ? '🔥 Disciplinado. Tu yo del futuro ya te está aplaudiendo.' :
                    '🚀 Wow, ¿eres real? A este ritmo te retiras antes que los demás se den cuenta.'}
          </p>

          <button
            type="button"
            onClick={applyIndiceToChallenge18}
            className="indice-link-challenge"
            data-testid="indice-link-challenge-18"
          >
            <ArrowRight className="w-4 h-4" />
            <span>Ver cuánto serían ${parseInt(indiceData.auto).toLocaleString('es-MX')}/mes en 18 meses</span>
          </button>
        </div>
      )}
    </CardContent>
  </Card>
);

export default SavingsIndexCard;
